import { useCreationTab } from '../creation-tab-context';
import { Column, Row } from 'app/design/primitives/box';
import { useFormContext } from 'app/hooks/form';
import { useScopedTranslation } from 'app/hooks/i18n';
import React from 'react';
import { useWatch } from 'react-hook-form';
import { Text } from 'app/design/primitives/text';
import { useAllCategories, useAllServiceCategories, useSimplifiedServices } from 'app/network/category';
import { type CreateListingDTO, ListingSubtype } from 'app/types/listing';

import { useTranslation } from 'react-i18next';
import { getI18nLanguage } from 'app/lib/language';
import { useNavigation } from 'app/hooks/navigation';
import { Button, PressableType } from 'app/design/primitives/button';

import { type CategoryIcons, CategoryTab } from 'app/design/categories/tab';
import { createTheme as createDSTheme } from 'app/shopify';
import { IconChevronRight } from '@tabler/icons-react-native';
import { ICON_STROKE_WIDTH } from 'app/utils/constants';
const theme = createDSTheme();

export function CategorySection({
  goBackTo,
  listingId,
}: {
  goBackTo: 'new' | 'publish' | 'one-listing' | 'edit-listing';
  listingId?: string;
}) {
  const { t } = useScopedTranslation('new', { nsMode: 'fallback' });

  const creationTab = useCreationTab();
  const { control } = useFormContext<CreateListingDTO>();
  const [watchSubtype, watchedCategoryId, watchedServiceCategoryId] = useWatch({
    control,
    exact: true,
    name: ['subtype', 'categoryPrimaryId', 'serviceCategoryId'],
  });
  const isService = watchSubtype === ListingSubtype.service;
  const simplifiedServices = useSimplifiedServices();

  if (isService && simplifiedServices) {
    return null;
  }

  return (
    <Column gap="spacing.general.x2">
      <Row gap="spacing.general.x1">
        <Text color="text.primary" fontWeight="700" variant="bodyBase">
          {t('categorySection.primary.title')}
        </Text>
        <Text color="text.primary" variant="bodyBase">
          {goBackTo === 'new' && !isService
            ? `(${t('optionale', { ns: 'common' })})`
            : `(${t('mandatory', { ns: 'common' })})*`}
        </Text>
      </Row>
      <CategoryPicker
        goBackTo={creationTab && goBackTo === 'new' ? 'publish' : goBackTo}
        isService={isService}
        listingId={listingId}
        selectedCategoryId={isService ? watchedServiceCategoryId : watchedCategoryId}
      />
    </Column>
  );
}

function CategoryPicker({
  isService,
  selectedCategoryId,
  goBackTo,
  listingId,
  onPress,
}: {
  isService: boolean;
  selectedCategoryId?: string | null;
  goBackTo: 'new' | 'publish' | 'one-listing' | 'edit-listing' | 'set-atom-then-close';
  listingId?: string;
  onPress?: () => void;
}) {
  const { t } = useTranslation('new');
  const i18nLanguage = getI18nLanguage();
  const navigation = useNavigation();

  const { data: serviceCategories } = useAllServiceCategories();
  const { data: categories } = useAllCategories();

  const selectedCategory = isService
    ? serviceCategories?.map((c) => ({ ...c, icon: undefined })).find((c) => c.id === selectedCategoryId)
    : categories
        ?.map((c) => ({ id: c.id, name: c.name[i18nLanguage], icon: c.icon }))
        .find((c) => c.id === selectedCategoryId);

  const navigateToServiceCategory = () => {
    navigation.navigate(
      'serviceCategories',
      { goBackTo, listingId, selectedCategory: selectedCategoryId ?? undefined },
      { pop: true },
    );
  };

  const navigateToCategorySelection = () => {
    navigation.navigate(
      'categories',
      { goBackTo, listingId, selectedCategory: selectedCategoryId ?? undefined },
      { pop: true },
    );
  };

  return (
    <Button
      onPress={onPress ?? (isService ? navigateToServiceCategory : navigateToCategorySelection)}
      pressableType={PressableType.GestureHandler}
      testID="new-listing-category-picker"
      variant="plain"
    >
      <Row
        alignItems="center"
        borderColor="border.secondary"
        borderRadius="radius.lg"
        borderWidth={1}
        flex={1}
        minWidth={0}
        padding="spacing.general.x2"
        style={{
          height: 48,
          borderRadius: 24,
          backgroundColor: selectedCategory ? theme.colors['surface.inverse.tertiary-neutral'] : undefined,
        }}
        width="100%"
      >
        {selectedCategory ? (
          <CategoryTab category={selectedCategory.icon as CategoryIcons} size={24} />
        ) : (
          <CategoryTab category="other" color={theme.colors['text.tertiary']} size={24} />
        )}
        <Text
          flex={1}
          fontSize={16}
          fontWeight={selectedCategory ? '700' : undefined}
          minWidth={0}
          paddingLeft="spacing.general.x2"
        >
          {selectedCategory?.name
            ? isService
              ? t(`serviceCategories.${selectedCategory.name}.name`, { ns: 'common' })
              : selectedCategory.name
            : t('categorySection.primary.placeholder')}
        </Text>
        <IconChevronRight strokeWidth={ICON_STROKE_WIDTH} size={24} color={theme.colors['text.primary']} />
      </Row>
    </Button>
  );
}
